/**
 * MCP 鍵発行ウィザード（UCX-04）
 *
 * 先方（非エンジニアの役員等）が自走で MCP 鍵を発行できるよう、
 * 「対象者選択 → 権限プリセット選択 → 発行」の 3 step に単純化したフロー。
 * 既存の principalRef 手入力 + tool/source 生トグル 22 項目（AccessManagement.tsx）は
 * 「詳細設定」として温存し、このウィザードは置き換えではなく追加の主導線。
 *
 * spec: docs/spec/04-security.md §12.6/§12.10
 *   - UI で発行した鍵は明示 allow-list（policy 皆無を「制限なし」にフォールバックしない）
 *   - rawKey は一度だけ表示・非永続（Modal を閉じると state から消える）
 */

import { runtimeConfigValue } from '../lib/runtime-config'
import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components'
import {
  useCreateAccessPolicy,
  useCreateMcpToken,
  useDeleteAccessPolicy,
  useUserSlots,
} from '@/hooks/useAccessManagement'
import type { AccessPolicy } from '@/types/api'
import {
  buildClaudeMcpAddCommand,
  buildPluginInstallCommand,
  buildPresetPolicyEntries,
  PRESET_DESCRIPTIONS,
  PRESET_LABELS,
  resolveMcpServerUrl,
  type AccessWizardPreset,
} from '@/lib/access-policy-presets'
import type { HokageUserSlotOption } from '@/types/api'

type WizardStep = 1 | 2 | 3

interface IssuedResult {
  name: string
  userSlot: string
  rawKey: string
  command: string
  installCommand: string
}

const PRESET_OPTIONS: AccessWizardPreset[] = ['full', 'numbers-only', 'no-meeting']

export interface McpKeyIssueWizardProps {
  workspaceId: string
}

/** state を丸ごと初期化する（rawKey/command を含め、完了後は必ずここへ戻す）。 */
function initialState() {
  return {
    step: 1 as WizardStep,
    selectedUserSlot: null as string | null,
    preset: null as AccessWizardPreset | null,
    tokenName: '',
    issuedResult: null as IssuedResult | null,
    issueError: null as string | null,
  }
}

export function McpKeyIssueWizard({ workspaceId }: McpKeyIssueWizardProps) {
  const userSlotsQuery = useUserSlots(workspaceId)
  const createToken = useCreateMcpToken(workspaceId)
  const createPolicy = useCreateAccessPolicy(workspaceId)
  const deletePolicy = useDeleteAccessPolicy(workspaceId)

  const [state, setState] = useState(initialState)
  const { step, selectedUserSlot, preset, tokenName, issuedResult, issueError } = state

  const userSlots: HokageUserSlotOption[] = userSlotsQuery.data?.userSlots ?? []
  const selectedOption = userSlots.find((option) => option.userSlot === selectedUserSlot) ?? null

  function resetWizard() {
    setState(initialState())
  }

  function goToStep(next: WizardStep) {
    setState((prev) => ({ ...prev, step: next }))
  }

  function selectUserSlot(userSlot: string) {
    const option = userSlots.find((o) => o.userSlot === userSlot)
    setState((prev) => ({
      ...prev,
      selectedUserSlot: userSlot,
      tokenName: prev.tokenName || `${option?.displayName ?? userSlot} MCP 鍵`,
    }))
  }

  function selectPreset(next: AccessWizardPreset) {
    setState((prev) => ({ ...prev, preset: next }))
  }

  async function handleIssue() {
    const trimmedName = tokenName.trim()
    if (!selectedUserSlot || !preset || !trimmedName) return

    setState((prev) => ({ ...prev, issueError: null, issuedResult: null }))

    // UAC-FIX-02: preset は allow に加えて deny entry（会議データ不可プリセットの
    // 会議系 source 遮断）も含むため、effect は entry の値をそのまま渡す。
    const entries = buildPresetPolicyEntries(preset)
    const createdPolicies: AccessPolicy[] = []
    const policyResults = await Promise.allSettled(
      entries.map((entry) =>
        createPolicy.mutateAsync({
          resourceEngine: entry.resourceEngine,
          resourceKind: entry.resourceKind,
          resourceId: entry.resourceId,
          principalRef: selectedUserSlot,
          actions: ['view'],
          effect: entry.effect,
        }),
      ),
    )

    for (const result of policyResults) {
      if (result.status === 'fulfilled') createdPolicies.push(result.value)
    }

    if (policyResults.some((result) => result.status === 'rejected')) {
      await Promise.allSettled(createdPolicies.map((policy) => deletePolicy.mutateAsync(policy.id)))
      setState((prev) => ({
        ...prev,
        issueError: '権限設定に失敗したため、鍵は発行していません。設定を確認して再度お試しください。',
      }))
      return
    }

    let rawKey: string
    try {
      const result = await createToken.mutateAsync({ name: trimmedName, userSlot: selectedUserSlot })
      rawKey = result.rawKey
    } catch {
      await Promise.allSettled(createdPolicies.map((policy) => deletePolicy.mutateAsync(policy.id)))
      setState((prev) => ({ ...prev, issueError: '鍵の発行に失敗しました' }))
      return
    }

    const mcpUrl = resolveMcpServerUrl(
      runtimeConfigValue('VITE_MCP_SERVER_URL', import.meta.env.VITE_MCP_SERVER_URL),
    )
    const command = buildClaudeMcpAddCommand({ mcpUrl, rawKey })
    const installCommand = buildPluginInstallCommand(mcpUrl)

    setState((prev) => ({
      ...prev,
      issuedResult: { name: trimmedName, userSlot: selectedUserSlot, rawKey, command, installCommand },
    }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>MCP 鍵発行ウィザード</CardTitle>
        <Badge tone="muted">Step {step} / 3</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">発行する相手を選んでください</p>
            {userSlotsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">読み込んでいます...</p>
            ) : userSlotsQuery.error ? (
              <p className="text-sm text-destructive">userSlot 一覧の取得に失敗しました</p>
            ) : userSlots.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                選択可能な対象者がありません。運用担当者に user-directory の設定を確認してください。
              </p>
            ) : (
              <div className="space-y-2">
                {userSlots.map((option) => (
                  <label
                    key={option.userSlot}
                    className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 hover:bg-muted/50"
                  >
                    <input
                      type="radio"
                      name="wizard-user-slot"
                      value={option.userSlot}
                      checked={selectedUserSlot === option.userSlot}
                      onChange={() => selectUserSlot(option.userSlot)}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-medium">{option.displayName}</span>
                      {option.departments.length > 0 && (
                        <span className="block text-xs text-muted-foreground">
                          {option.departments.join(' / ')}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => goToStep(2)}
                disabled={!selectedUserSlot}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                次へ
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {selectedOption?.displayName ?? selectedUserSlot} に付与する権限を選んでください
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {PRESET_OPTIONS.map((option) => (
                <label
                  key={option}
                  className="flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-3 hover:bg-muted/50"
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="wizard-preset"
                      value={option}
                      checked={preset === option}
                      onChange={() => selectPreset(option)}
                    />
                    <span className="text-sm font-medium">{PRESET_LABELS[option]}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{PRESET_DESCRIPTIONS[option]}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              tool / source を個別に調整したい場合は、発行後にページ下部の「詳細設定」から変更できます。
            </p>
            <div className="flex justify-between">
              <button
                onClick={() => goToStep(1)}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                戻る
              </button>
              <button
                onClick={() => goToStep(3)}
                disabled={!preset}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                次へ
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="wizard-token-name" className="text-sm font-medium">
                鍵の名前
              </label>
              <input
                id="wizard-token-name"
                value={tokenName}
                onChange={(e) => setState((prev) => ({ ...prev, tokenName: e.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            {issueError && <p className="text-xs text-destructive">{issueError}</p>}
            <div className="flex justify-between">
              <button
                onClick={() => goToStep(2)}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                戻る
              </button>
              <button
                onClick={() => void handleIssue()}
                disabled={
                  createToken.isPending ||
                  createPolicy.isPending ||
                  deletePolicy.isPending ||
                  !tokenName.trim()
                }
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {createToken.isPending || createPolicy.isPending ? '発行中...' : '発行'}
              </button>
            </div>
          </div>
        )}
      </CardContent>

      <Modal
        open={issuedResult !== null}
        onOpenChange={(open) => {
          if (!open) resetWizard()
        }}
      >
        <ModalHeader>
          <ModalTitle>MCP 鍵を発行しました</ModalTitle>
          <ModalDescription>
            この鍵は今だけ表示されます。閉じると二度と表示できません。安全な場所に保存してください。
          </ModalDescription>
        </ModalHeader>
        {issuedResult && (
          <div className="space-y-3">
            <p className="text-sm font-medium">{issuedResult.name}</p>
            <CopyableField label="鍵" value={issuedResult.rawKey} />
            <CopyableField
              label="① プラグインをインストール（鍵は不要・初回のみ）"
              value={issuedResult.installCommand}
            />
            <CopyableField label="② MCP 接続（発行された鍵入り）" value={issuedResult.command} />
            <p className="text-xs text-muted-foreground">
              接続手順の詳細は社内 Wiki を参照してください。
            </p>
          </div>
        )}
        <ModalFooter>
          <button
            onClick={resetWizard}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            保存したので閉じる
          </button>
        </ModalFooter>
      </Modal>
    </Card>
  )
}

function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
        <code className="flex-1 overflow-x-auto text-xs">{value}</code>
        <button
          onClick={() => void handleCopy()}
          className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'コピー済み' : 'コピー'}
        </button>
      </div>
    </div>
  )
}
